import React, { useEffect, useState } from 'react';
import { Copy, Share2, ChevronDown, ShieldOff, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { useOs2, useRangeLabel, PageHead, Card, Stat, money, Th, Td, fmtDateTime, LoadingState } from './os2ui';

/* Affiliates — "Which partners drive real revenue, and who needs attention?" */

function CampaignKit({ isEs }) {
  const [kit, setKit] = useState(null);
  useEffect(() => {
    authFetch('/affiliates/campaign/kit').then((r) => r.json()).then(setKit).catch(() => {});
  }, []);
  if (!kit?.kit) return null;
  const k = kit.kit; const l = isEs ? 'es' : 'en';
  const items = [
    ['LinkedIn', k.social.linkedin[l]],
    ['Facebook / IG', k.social.facebook_instagram[l]],
    ['X', k.social.x_twitter[l]],
    [isEs ? 'Foros' : 'Forums', `${k.forum_post[l].title}\n\n${k.forum_post[l].body}`],
    ['Email', `${k.email_snippet[l].subject}\n\n${k.email_snippet[l].body}`],
    ['WhatsApp', k.whatsapp[l]],
  ];
  return (
    <Card className="p-6" data-testid="aff-campaign-kit">
      <h2 className="text-[15px] font-semibold tracking-tight mb-1 flex items-center gap-2">
        <Share2 className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Kit de campaña — listo para publicar' : 'Campaign kit — ready to publish'}
      </h2>
      <p className="text-[12px] text-gray-400 mb-3">{isEs ? 'Alta gratis · 30% recurrente · cookie 90 días. Copiado con un clic:' : 'Free signup · 30% recurring · 90-day cookie. One-click copy:'}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(([label, text]) => (
          <button key={label}
            onClick={() => navigator.clipboard.writeText(text).then(() => toast.success(`${label} ✓`))}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-gray-200 bg-white hover:bg-[#F77F11] hover:border-[#F77F11] hover:text-white text-[12.5px] font-semibold text-gray-600 transition-colors"
            data-testid={`aff-kit-copy-${label.split(' ')[0].toLowerCase()}`}>
            <Copy className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>
    </Card>
  );
}

function PartnerActions({ a, isEs, reload }) {
  const [busy, setBusy] = useState('');
  const act = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      toast.success(isEs ? `${label} ✓ — Eva y el equipo AI informados` : `${label} ✓ — Eva & AI Team informed`);
      reload();
    } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(''); }
  };
  const revoke = () => {
    const reason = window.prompt(isEs ? 'Motivo de la revocación:' : 'Revocation reason:', 'admin_decision');
    if (reason === null) return;
    act(isEs ? 'Revocado' : 'Revoked', async () => {
      const r = await authFetch(`/affiliates/${a.id}/revoke`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason || 'admin_decision' }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    });
  };
  const approve = () => act(isEs ? 'Aprobado' : 'Approved', async () => {
    const r = await authFetch(`/affiliates/${a.id}/status`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'approved' }) });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
  });
  const removeForever = () => {
    if (!window.confirm(isEs
      ? `⚠️ ¿ELIMINAR DEFINITIVAMENTE a ${a.name} (${a.ref_code})?\n\nNo se puede deshacer. Referidos y comisiones se conservan para contabilidad. Eva será informada.`
      : `⚠️ PERMANENTLY DELETE ${a.name} (${a.ref_code})?\n\nCannot be undone. Referrals & commissions are kept for accounting. Eva will be informed.`)) return;
    act(isEs ? 'Eliminado' : 'Deleted', async () => {
      const r = await authFetch(`/affiliates/${a.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    });
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-orange-50/40 border-t border-orange-100" data-testid={`aff-actions-${a.ref_code}`}>
      <span className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mr-1">{isEs ? 'Acciones superadmin' : 'Superadmin actions'}:</span>
      <span className="text-[12px] text-gray-500">{a.email}</span>
      {a.status !== 'approved' && (
        <button onClick={approve} disabled={!!busy} className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[12px] font-semibold transition-colors disabled:opacity-50" data-testid={`aff-approve-${a.ref_code}`}>
          <Check className="w-3.5 h-3.5" /> {isEs ? 'Aprobar / Reinstaurar' : 'Approve / Reinstate'}
        </button>
      )}
      {a.status === 'approved' && (
        <button onClick={revoke} disabled={!!busy} className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 text-[12px] font-semibold transition-colors disabled:opacity-50" data-testid={`aff-revoke-${a.ref_code}`}>
          <ShieldOff className="w-3.5 h-3.5" /> {isEs ? 'Revocar acceso' : 'Revoke access'}
        </button>
      )}
      <button onClick={removeForever} disabled={!!busy} className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 text-[12px] font-bold transition-colors disabled:opacity-50" data-testid={`aff-delete-${a.ref_code}`}>
        <Trash2 className="w-3.5 h-3.5" /> {isEs ? 'Eliminar definitivamente' : 'Delete permanently'}
      </button>
      <a href="/admin/affiliates" className="ml-auto text-[12px] font-semibold text-[#F77F11] hover:underline">{isEs ? 'Panel completo de afiliados →' : 'Full affiliate panel →'}</a>
    </div>
  );
}

export default function AffiliatesOsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/affiliates');
  const { label } = useRangeLabel(isEs);
  const [open, setOpen] = useState(null);
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const t = d.totals; const f = d.funnel;
  const jump = (id) => () => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('ring-2', 'ring-[#F77F11]', 'ring-offset-2');
    setTimeout(() => el.classList.remove('ring-2', 'ring-[#F77F11]', 'ring-offset-2'), 1800);
  };

  return (
    <div data-testid="os2-affiliates">
      <PageHead
        title={isEs ? 'Afiliados' : 'Affiliates'}
        question={isEs ? '¿Qué partners generan ingresos reales, y quién necesita atención?' : 'Which partners drive real revenue, and who needs attention?'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="Partner Program"
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <button onClick={jump('aff-ranking')} className="text-left" data-testid="aff-total-jump"><Stat label={isEs ? 'Partners' : 'Partners'} value={t.affiliates} testid="aff-total" accent isEs={isEs} scope="current" source="Kitxens DB" /></button>
        <button onClick={jump('aff-ranking')} className="text-left" data-testid="aff-clicks-jump"><Stat label={isEs ? 'Clics' : 'Clicks'} value={t.clicks_period} testid="aff-clicks" isEs={isEs} scope="period" source="Kitxens DB" /></button>
        <button onClick={jump('aff-ranking')} className="text-left" data-testid="aff-referrals-jump"><Stat label={isEs ? 'Referidos' : 'Referrals'} value={t.referrals} testid="aff-referrals" help="referrals" isEs={isEs} scope="lifetime" source="Kitxens DB" /></button>
        <button onClick={jump('aff-ranking')} className="text-left" data-testid="aff-pending-jump"><Stat label={isEs ? 'Comisiones pendientes' : 'Pending commissions'} value={money(t.pending_commissions)} testid="aff-pending" help="payouts" isEs={isEs} scope="current" source="Kitxens DB" /></button>
        <button onClick={jump('aff-ranking')} className="text-left" data-testid="aff-approved-jump"><Stat label={isEs ? 'Aprobadas' : 'Approved'} value={money(t.approved_commissions)} testid="aff-approved" isEs={isEs} scope="current" /></button>
        <button onClick={jump('aff-activity')} className="text-left" data-testid="aff-paid-jump"><Stat label={isEs ? 'Pagadas' : 'Paid'} value={money(t.paid_commissions)} testid="aff-paid" isEs={isEs} scope="lifetime" /></button>
      </div>
      <p className="mt-1.5 text-[10.5px] text-gray-400">{isEs ? 'Toca cualquier tarjeta para saltar al detalle correspondiente ↓' : 'Tap any card to jump to its detail below ↓'}</p>

      {/* Recruitment funnel */}
      <Card className="mt-4 p-5" data-testid="aff-funnel">
        <p className="text-[11px] tracking-wider uppercase text-gray-400 font-medium mb-3">{isEs ? 'Funnel de captación de partners' : 'Partner recruitment funnel'}</p>
        <div className="flex flex-wrap items-center gap-2">
          {[[isEs ? 'Interés' : 'Interest', f.interest], [isEs ? 'Solicitudes' : 'Applied', f.applied],
            [isEs ? 'Registrados' : 'Registered', f.registered], [isEs ? 'Aprobados' : 'Approved', f.approved],
            [isEs ? 'Conversiones' : 'Conversions', f.conversions]].map(([l, v], i) => (
            <React.Fragment key={l}>
              {i > 0 && <span className="text-gray-300">→</span>}
              <span className="rounded-xl px-3 py-2 text-center text-white transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: ['linear-gradient(135deg,#475569,#64748B)', 'linear-gradient(135deg,#0369A1,#38BDF8)', 'linear-gradient(135deg,#6D28D9,#A78BFA)', 'linear-gradient(135deg,#C2570A,#F77F11)', 'linear-gradient(135deg,#047857,#34D399)'][i] }}>
                <span className="block text-lg font-black tabular-nums leading-none">{v}</span>
                <span className="block text-[9.5px] uppercase tracking-wider text-white/80 font-bold mt-0.5">{l}</span>
              </span>
            </React.Fragment>
          ))}
          {Object.keys(d.countries || {}).length > 0 && (
            <span className="ml-auto text-[12px] text-gray-400">
              {Object.entries(d.countries).map(([c, n]) => `${c} (${n})`).join(' · ')}
            </span>
          )}
        </div>
      </Card>

      {/* Ranking */}
      <Card className="mt-4 overflow-hidden" data-testid="aff-ranking">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-[15px] font-semibold tracking-tight">{isEs ? 'Ranking de partners' : 'Partner ranking'}</h2>
          <span className="text-[12px] text-gray-400">{isEs ? `ordenado por clics · ${label}` : `sorted by clicks · ${label}`}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <Th>#</Th><Th>{isEs ? 'Partner' : 'Partner'}</Th><Th>{isEs ? 'Estado' : 'Status'}</Th>
              <Th>{isEs ? 'País' : 'Country'}</Th><Th>{isEs ? `Clics (${label} / total)` : `Clicks (${label} / total)`}</Th>
              <Th>{isEs ? 'Referidos' : 'Referrals'}</Th><Th>{isEs ? 'Conversiones' : 'Conversions'}</Th>
              <Th>{isEs ? 'Pendiente' : 'Pending'}</Th><Th>{isEs ? 'Pagado' : 'Paid'}</Th>
            </tr></thead>
            <tbody>
              {d.ranking.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[13px] text-gray-400" data-testid="aff-ranking-empty">
                  {isEs ? 'Aún no hay partners con actividad — cuando un partner registre clics o referidos aparecerá aquí.' : 'No partners with activity yet — once a partner logs clicks or referrals they will appear here.'}
                </td></tr>
              )}
              {d.ranking.map((a, i) => (
                <React.Fragment key={a.ref_code}>
                  <tr onClick={() => setOpen(open === a.ref_code ? null : a.ref_code)}
                    className="hover:bg-orange-50/40 transition-colors cursor-pointer" data-testid={`aff-row-${a.ref_code}`}>
                    <Td className="text-gray-400 tabular-nums">{i + 1}</Td>
                    <Td>
                      <p className="font-semibold text-gray-900 flex items-center gap-1">{a.name}
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-300 transition-transform ${open === a.ref_code ? 'rotate-180' : ''}`} />
                      </p>
                      <p className="text-[11px] text-gray-400">{a.ref_code}</p>
                    </Td>
                    <Td><span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium ${a.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : a.status === 'revoked' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{a.status}</span></Td>
                    <Td>{[a.city, a.country].filter(Boolean).join(', ') || '—'}</Td>
                    <Td className="min-w-[130px]">
                      <span className="tabular-nums font-semibold">{a.clicks_period} <span className="text-gray-300 font-normal">/ {a.clicks}</span></span>
                      <span className="mt-1 block h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <span className="block h-full rounded-full" style={{ width: `${Math.max(4, ((a.clicks_period || 0) / Math.max(1, ...d.ranking.map((x) => x.clicks_period || 0))) * 100)}%`, background: 'linear-gradient(90deg,#C2570A,#F77F11)' }} />
                      </span>
                    </Td>
                    <Td className="tabular-nums">{a.referrals}</Td>
                    <Td className="tabular-nums">{a.conversions}</Td>
                    <Td className="tabular-nums">{money(a.commissions.pending)}</Td>
                    <Td className="tabular-nums">{money(a.commissions.paid)}</Td>
                  </tr>
                  {open === a.ref_code && (
                    <tr><td colSpan={9} className="p-0"><PartnerActions a={a} isEs={isEs} reload={reload} /></td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CampaignKit isEs={isEs} />
        <Card className="p-6" data-testid="aff-activity">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3">{isEs ? 'Actividad reciente' : 'Recent activity'}</h2>
          {(d.activity || []).map((e, i) => (
            <p key={i} className="text-[12.5px] text-gray-500 py-1 border-b border-gray-100 last:border-0 truncate">
              <span className="text-gray-300 tabular-nums">{fmtDateTime(e.at)}</span>
              <span className="text-[#b45309] font-medium"> {e.type.replace('AFFILIATE_', '')}</span>
              {e.detail && <span> · {e.detail}</span>}
            </p>
          ))}
        </Card>
      </div>
    </div>
  );
}
